/* Three purchase choices on active guides, scoped to their existing product form. */
if (!customElements.get('diy-ebook-offers')) {
  customElements.define('diy-ebook-offers', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.config = JSON.parse(this.querySelector('[data-offers-config]').textContent);
      if (this.config.products.some(product => !product.variants.length)) return;
      this.ready = true;
      this.form = this.closest('form');
      this.section = this.closest('.shopify-section');
      this.choice = 0;
      this.lang = this.config.products[0].variants.find(v => v.id === this.config.selectedVariant)?.lang || 'fr';
      this.form.addEventListener('formdata', event => {
        const variant = this.selectedVariant();
        if (variant?.available) event.formData.set('id', String(variant.id));
        this.querySelectorAll('input[type=radio]').forEach(input => event.formData.delete(input.name));
      });
      this.addEventListener('change', event => {
        if (!event.target.matches('input[type=radio]')) return;
        this.choice = Number(event.target.value);
        this.paint();
      });
      this.onEditionChange = event => {
        const picker = event.target.closest('variant-radios, variant-selects');
        if (!picker) return;
        const id = picker.currentVariant?.id;
        const variant = this.config.products[0].variants.find(v => v.id === id);
        if (variant) { this.lang = variant.lang; this.paint(); }
      };
      this.section.addEventListener('change', this.onEditionChange);
      this.buttonObserver = new MutationObserver(() => this.paintButton());
      this.buttonObserver.observe(this.form.querySelector('.product-form__buttons'), { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
      this.section.classList.add('diy-has-ebook-offers');
      this.hidden = false;
      this.paint();
    }
    disconnectedCallback() {
      this.buttonObserver?.disconnect();
      this.section?.removeEventListener('change', this.onEditionChange);
    }
    variant(product) { return product.variants.find(v => v.lang === this.lang); }
    selectedVariant() { return this.variant(this.config.products[this.choice]); }
    money(cents) {
      return new Intl.NumberFormat(this.lang === 'fr' ? 'fr-FR' : 'en-GB', { style: 'currency', currency: this.config.currency }).format(cents / 100).replace(/^€\s*(.+)$/, '$1\u00a0€');
    }
    paint() {
      const fr = this.lang === 'fr';
      const set = (selector, value, root = this) => { const target = root.querySelector(selector); if (target) target.textContent = value; };
      set('[data-offer-heading]', fr ? 'Choisissez votre formule' : 'Choose your option');
      set('[data-offer-intro]', fr ? 'Un guide, un parcours ou toute la collection.' : 'One guide, a learning path or the complete collection.');
      this.querySelectorAll('.diy-offers__column').forEach((column, index) => {
        const product = this.config.products[index];
        const variant = this.variant(product);
        const radio = column.querySelector('input');
        radio.disabled = !variant?.available;
        if (index === this.choice && radio.disabled) {
          this.choice = this.config.products.findIndex(p => this.variant(p)?.available);
        }
        const title = product.titles[this.lang];
        set('[data-offer-title]', title, column);
        set('[data-offer-count]', product.count + (product.count === 1 ? ' guide' : ' guides'), column);
        set('[data-offer-badge]', index === 1 ? (fr ? 'VOTRE PACK' : 'YOUR PACK') : index === 2 ? (fr ? 'TOUT INCLUS' : 'ALL INCLUDED') : (fr ? 'À L’UNITÉ' : 'SINGLE GUIDE'), column);
        set('[data-offer-price]', variant ? this.money(variant.price) : '—', column);
        const baseline = product.baseline[this.lang];
        const hasSaving = variant && baseline > variant.price;
        set('[data-offer-baseline]', hasSaving ? this.money(baseline) : '', column);
        const saving = hasSaving ? Math.round((1 - variant.price / baseline) * 100) : 0;
        set('[data-offer-saving]', hasSaving ? (fr ? 'Économisez ' : 'Save ') + saving + ' %' : (fr ? 'Ce guide uniquement' : 'This guide only'), column);
        if (this.dataset.design === 'v13') {
          const packNames = {
            'pack-entreprise-fr': { fr: 'Pack\nEntreprise', en: 'Business\nPack' },
            'pack-acquisition-contenu-fr': { fr: 'Pack\nAcquisition', en: 'Acquisition\nPack' },
            'pack-expert-fr': { fr: 'Pack\nSEO Expert', en: 'SEO Expert\nPack' }
          };
          const names = [fr ? 'Guide\nseul' : 'Single\nguide', packNames[this.config.packHandle][this.lang], fr ? 'Collection\nintégrale' : 'Complete\nCollection'];
          set('[data-offer-title]', names[index], column);
          set('[data-offer-count]', product.count + (product.count === 1 ? ' guide' : ' guides'), column);
          set('[data-offer-best]', fr ? 'MEILLEURE OFFRE' : 'BEST VALUE', column);
          set('[data-offer-saved-amount]', hasSaving ? this.money(baseline - variant.price) + (fr ? ' économisés' : ' saved') : '', column);
          set('[data-offer-saving]', hasSaving ? '−' + saving + ' %' : (fr ? 'Ce guide uniquement' : 'This guide only'), column);
          column.querySelector('[data-offer-saving]').classList.toggle('has-saving', Boolean(hasSaving));
          column.querySelector('[data-offer-baseline]').setAttribute('aria-label', hasSaving ? (fr ? 'Prix des guides achetés séparément : ' : 'Guides bought separately: ') + this.money(baseline) : '');
        }
        const image = column.querySelector('[data-offer-cover]');
        if (image.getAttribute('src') !== product.covers[this.lang]) image.src = product.covers[this.lang];
        image.alt = this.dataset.design === 'v13' && index === 0 ? (fr ? 'Guide seul' : 'Single guide') : title;
        const details = column.querySelector('[data-offer-details]');
        if (details) {
          details.textContent = this.dataset.design === 'v13' ? (fr ? 'Voir les guides ↗' : 'View guides ↗') : (fr ? 'Voir les guides inclus ↗' : 'View included guides ↗');
          details.href = product.url + '?variant=' + (variant?.id || '');
          details.setAttribute('aria-label', (fr ? 'Voir ' : 'View ') + title);
        }
      });
      this.querySelectorAll('input[type=radio]').forEach(input => { input.checked = Number(input.value) === this.choice; });
      const selected = this.config.products[this.choice];
      const count = selected.count;
      const variant = this.selectedVariant();
      set('[data-offer-summary]', selected.titles[this.lang] + ' · ' + (variant ? this.money(variant.price) : '—') + ' · ' + (fr ? 'Français' : 'English'));
      set('[data-offer-bonus]', count + (fr ? (count === 1 ? ' agent IA + ' : ' agents IA + ') : (count === 1 ? ' AI agent + ' : ' AI agents + ')) + count * 3 + (fr ? ' skills offerts' : ' free skills'));
      set('[data-offer-included]', this.choice === 0 ? (fr ? 'Le guide consulté, avec ses ressources.' : 'This guide, with its resources.') : (fr ? 'Ce guide est déjà inclus dans cette formule.' : 'This guide is already included in this option.'));
      set('[data-offer-payment]', fr ? 'Achat unique · PDF numérique' : 'One-time purchase · Digital PDF');
      this.form.classList.toggle('diy-offer-is-pack', this.choice > 0);
      this.dataset.language = this.lang;
      this.dataset.choice = String(this.choice);
      this.paintButton();
    }
    paintButton() {
      const button = this.form.querySelector('[type=submit][name=add]');
      const label = button?.querySelector('span');
      const variant = this.selectedVariant();
      if (!button || !label || !variant) return;
      const fr = this.lang === 'fr';
      const text = !variant.available ? (fr ? 'Indisponible' : 'Unavailable') : (fr ? 'Ajouter au panier' : 'Add to cart') + ' · ' + this.money(variant.price);
      if (label.textContent !== text) label.textContent = text;
      if (button.disabled === variant.available) button.disabled = !variant.available;
      const buyNow = this.form.querySelector('[data-diy-buy-now]');
      if (buyNow && buyNow.disabled === variant.available) buyNow.disabled = !variant.available;
    }
  });
}
